/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    Text,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import RegistrationFields from '../../components/forms/registration-fields'
import {useLocation} from 'react-router-dom'
import {AccountDrawerLogo, GarageBrandIcon} from '../icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import GoogleRecatpchaText from '../../components/google-recatpcha-text'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'

const RegisterForm = ({submitForm, clickSignIn = noop, form, prefix = ''}) => {
    const {app: appConfig} = getConfig()
    const location = useLocation()
    const isRegistrationPage = location.pathname.endsWith('/registration')
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()

    const isDuplicateCustomerError = form.formState.errors?.global?.isDuplicateCustomer

    return (
        <Fragment>
            {!isRegistrationPage && (
                <Stack justify="center" align="center" spacing={0} marginBottom={8}>
                    {shouldHideLoyaltyUI ? (
                        <GarageBrandIcon h="20px" w="auto" />
                    ) : (
                        <AccountDrawerLogo width="110px" height="auto" />
                    )}
                    {!shouldHideLoyaltyUI && (
                        <Text align="center" fontSize="md" fontWeight="medium">
                            <FormattedMessage
                                defaultMessage="Join, earn points, get spoiled"
                                id="login_form.message.join"
                            />
                        </Text>
                    )}
                </Stack>
            )}
            <form
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form-register"
                noValidate
            >
                <Stack spacing={8} px={{base: 4, lg: 0}}>
                    {form.formState.errors?.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {isDuplicateCustomerError ? (
                                    <FormattedMessage
                                        id="registration.error.duplicate_customer"
                                        defaultMessage="This email address already exists. Please try again or <signIn>sign in</signIn>. If the issue persists, please contact <customerService>Customer Service</customerService>."
                                        values={{
                                            signIn: (chunks) => (
                                                <ChakraLink
                                                    as="button"
                                                    type="button"
                                                    onClick={clickSignIn}
                                                    textDecoration="underline"
                                                    color="inherit"
                                                >
                                                    {chunks}
                                                </ChakraLink>
                                            ),
                                            customerService: (chunks) => (
                                                <ChakraLink
                                                    href={appConfig?.CUSTOMER_SERVICE_URL}
                                                    textDecoration="underline"
                                                    color="inherit"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {chunks}
                                                </ChakraLink>
                                            )
                                        }}
                                    />
                                ) : (
                                    form.formState.errors.global.message
                                )}
                            </Text>
                        </Alert>
                    )}
                    <RegistrationFields form={form} prefix={prefix} />
                    <Stack spacing={8}>
                        <Stack spacing={2}>
                            <Button
                                type="submit"
                                onClick={() => form.clearErrors('global')}
                                isLoading={form.formState.isSubmitting}
                            >
                                {shouldHideLoyaltyUI ? (
                                    <FormattedMessage
                                        defaultMessage="Join Now"
                                        id="login_form.action.join_now"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Join our Collectif"
                                        id="login_join.our.collectif"
                                    />
                                )}
                            </Button>
                            <GoogleRecatpchaText />
                        </Stack>

                        <Stack direction="row" spacing={1} justify="flex-start">
                            <Text fontSize="sm">
                                <FormattedMessage
                                    defaultMessage="Already have an account?"
                                    id="register_form.message.already_have_account"
                                />
                            </Text>
                            <Button variant="link" size="sm" onClick={clickSignIn}>
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="register_form.action.sign_in"
                                />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

RegisterForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    prefix: PropTypes.string
}

export default RegisterForm
